<template>
  <ProductColorPicker
    :items="siblingsColors"
    :is-limiting="true"
    :from-listing-page="false"
    :is-foldable="showPdpFoldableColors"
    class="mt-4"
    :selected-color-name="colorName"
  >
    <template #item="{ item }">
      <nuxt-link
        v-if="siblingPaths[item.id]"
        :replace="true"
        :to="siblingPaths[item.id]"
        :aria-label="
          getColorPickerAriaLabel(item.colors[0]?.label, $t('pdp.color'))
        "
        @click.capture="handleColorClick()"
      >
        <ColorCircle
          data-test-id="product-card-color-circle"
          :color="item.colors[0] as ProductColor"
          :has-active-dot="item.id === product.id"
          :decorative="true"
        />
      </nuxt-link>
      <span
        v-else
        class="inline-flex min-h-8 min-w-8 items-center justify-center"
        :aria-label="
          getColorPickerAriaLabel(item.colors[0]?.label, $t('pdp.color'))
        "
        :aria-current="item.id === product.id ? 'true' : undefined"
      >
        <ColorCircle
          data-test-id="product-card-color-circle"
          :color="item.colors[0] as ProductColor"
          :has-active-dot="item.id === product.id"
          :decorative="true"
        />
      </span>
    </template>
  </ProductColorPicker>
</template>

<script setup lang="ts">
import {
  getFirstAttributeValue,
  type Product,
  type ProductColor,
  type ProductSibling,
} from '@scayle/storefront-nuxt'
import usePpkContext from '../../../../../composables/usePpkContext'
import useRouteHelpers from '../../../../../composables/useRouteHelpers'
import { buildSiblingColorRoute } from '../../../../../utils/siblingColorRoute'
import { getColorPickerAriaLabel } from './getColorPickerAriaLabel'
import { getSiblingsColors, isDirectOrder } from '~/utils/product'

const props = defineProps({
  highlightedOrderNumber: {
    type: String as PropType<string>,
    default: '',
  },
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
})

const emit = defineEmits(['click:product'])

const route = useRoute()
const { toLocalePath } = useRouteHelpers()
const { showPdpFoldableColors } = useExperiments()
const { getProductEntry, useContextCheck } = usePpkContext()

// Keep the slug currently in the URL so the PDP page key (route.params.slug)
// stays constant across color switches — see buildSiblingColorRoute (ECOM-2207).
const currentSlug = computed(() =>
  Array.isArray(route.params.slug)
    ? (route.params.slug[0] ?? '')
    : route.params.slug,
)

const colorName = computed(
  () => getFirstAttributeValue(props.product.attributes, 'color')?.label,
)
const siblingsColors = computed(() => {
  // ECOM-2639: colour swatches depend on the viewed product's direct-order flag.
  // - Standard PDP (viewed product direct_order=no): hide direct-order siblings,
  //   so only standard colours surface.
  // - Direct-order PDP (viewed product direct_order=yes, e.g. reached via a
  //   direct-order article number): show every colour — both direct-order and
  //   standard siblings — so the full colour range stays available.
  // getProductSiblings always includes the viewed product itself, so its own
  // colour is shown regardless. Scoped here to keep getSiblingsColors' other
  // consumers (recommendations, PLP cards, the direct-order page) unaffected.
  const filteredProduct = isDirectOrder(props.product)
    ? props.product
    : {
        ...props.product,
        siblings: props.product.siblings?.filter(
          (sibling) => !isDirectOrder(sibling),
        ),
      }
  return getSiblingsColors(filteredProduct) as ProductSibling[]
})
const isContextSet = computed(
  () => !props.highlightedOrderNumber || useContextCheck(props.product).value,
)

const siblingPaths = computed(() => {
  const siblings = props.product.siblings || []
  const slug = currentSlug.value

  if (!isContextSet.value || typeof slug !== 'string') {
    return {}
  }

  return siblings.reduce(
    (acc, sibling) => {
      const productEntry = props.highlightedOrderNumber
        ? getProductEntry(sibling)
        : undefined
      if (props.highlightedOrderNumber && !productEntry) return acc

      const orderNumber = productEntry?.orderNumber

      acc[sibling.id] = toLocalePath(
        buildSiblingColorRoute(slug, sibling.id, orderNumber),
      ) as string
      return acc
    },
    {} as Record<number, string>,
  )
})

const handleColorClick = () => {
  emit('click:product')
}
</script>
